import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError(t("register.mismatch")); return; }
    if (password.length < 6) { setError(t("register.tooShort")); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { email, password });
      login(data.token, data.user);
      toast.success(t("register.created"));
      navigate("/profil");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-5 py-12 paper-noise">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#E5E0D8] p-8 shadow-[0_8px_24px_rgba(44,42,40,0.06)]">
        <div className="flex items-center gap-3 mb-6">
          <span className="bg-white border border-[#E5E0D8] rounded-xl p-1 inline-flex"><img src="/roadtogo-logo.png" alt="RoadToGo" className="h-9 w-auto object-contain" /></span>
          <span className="font-serif text-2xl text-[#2C2A28]">{t("register.title")}</span>
        </div>
        <p className="text-sm text-[#5C5854] mb-6">{t("register.subtitle")}</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8C857B]">{t("register.email")}</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                   className="mt-1 rounded-xl focus-visible:ring-[#E06D53]" data-testid="register-email" placeholder="you@example.com" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8C857B]">{t("register.password")}</Label>
            <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                   className="mt-1 rounded-xl focus-visible:ring-[#E06D53]" data-testid="register-password" placeholder={t("register.passwordHint")} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8C857B]">{t("register.confirm")}</Label>
            <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                   className="mt-1 rounded-xl focus-visible:ring-[#E06D53]" data-testid="register-confirm" placeholder="••••••••" />
          </div>
          {error && <p className="text-sm text-[#D62828]" data-testid="register-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full rounded-full h-11 bg-[#E06D53] hover:bg-[#C95B42] text-white" data-testid="register-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("register.submit")}
          </Button>
        </form>
        <p className="text-sm text-center text-[#5C5854] mt-6">
          {t("register.haveAccount")} <Link to="/connexion" className="text-[#E06D53] font-medium">{t("register.signin")}</Link>
        </p>
      </div>
    </div>
  );
}
